import React from "react";
import { InlineMath } from "react-katex";
import FractionInput, { formatLatexPreview } from "../../../../components/inputs/FractionInput";

function randInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function pick(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

function gcd(a, b) {
  let x = Math.abs(a);
  let y = Math.abs(b);
  while (y) [x, y] = [y, x % y];
  return x || 1;
}

function fractionLatex(numerator, denominator) {
  return `\\frac{${numerator}}{${denominator}}`;
}

function parseRawFraction(raw) {
  if (typeof raw !== "string") return null;
  const cleaned = raw.trim().replace(",", ".").replace(/\s+/g, "");
  const match = cleaned.match(/^(-?\d+)\/(\d+)$/);
  if (!match) return null;
  const numerator = Number(match[1]);
  const denominator = Number(match[2]);
  if (!Number.isInteger(numerator) || !Number.isInteger(denominator) || denominator === 0) {
    return null;
  }
  return { numerator, denominator };
}

function circleSectorPath(cx, cy, radius, startAngle, endAngle) {
  const start = {
    x: cx + radius * Math.cos(startAngle),
    y: cy + radius * Math.sin(startAngle),
  };
  const end = {
    x: cx + radius * Math.cos(endAngle),
    y: cy + radius * Math.sin(endAngle),
  };
  const largeArc = endAngle - startAngle > Math.PI ? 1 : 0;
  return `M ${cx} ${cy} L ${start.x} ${start.y} A ${radius} ${radius} 0 ${largeArc} 1 ${end.x} ${end.y} Z`;
}

function pickShapeForDenominator(denominator) {
  const shapes = ["strip"];
  if (denominator <= 8) shapes.push("circle");
  if ([4, 9, 16].includes(denominator)) shapes.push("grid");
  return pick(shapes);
}

function FractionShape({
  shape,
  denominator,
  numerator,
  selectedCount,
  selectedIndices,
  interactive = false,
  onToggleIndex,
}) {
  const selectedSet = new Set(
    Array.isArray(selectedIndices)
      ? selectedIndices
      : Array.from({ length: typeof selectedCount === "number" ? selectedCount : numerator }, (_, index) => index)
  );
  const activeColor = "#f59e0b";
  const inactiveColor = "rgba(255,255,255,0.08)";
  const strokeColor = "currentColor";
  const wrapperStyle = { marginBottom: 14 };

  if (shape === "strip") {
    const width = 260;
    const height = 54;
    const cell = width / denominator;
    return (
      <div style={wrapperStyle}>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        width="100%"
        style={{ maxWidth: 300, color: "var(--color-text)" }}
      >
        {Array.from({ length: denominator }).map((_, index) => {
          const selected = selectedSet.has(index);
          return (
            <rect
              key={index}
              x={index * cell}
              y={0}
              width={cell}
              height={height}
              fill={selected ? activeColor : inactiveColor}
              style={{ cursor: interactive ? "pointer" : "default" }}
              onClick={() => interactive && onToggleIndex && onToggleIndex(index)}
            />
          );
        })}
        {Array.from({ length: denominator + 1 }).map((_, index) => (
          <line
            key={`line-${index}`}
            x1={index * cell}
            y1={0}
            x2={index * cell}
            y2={height}
            stroke={strokeColor}
            strokeWidth="1.5"
          />
        ))}
        <rect x={0} y={0} width={width} height={height} fill="none" stroke={strokeColor} strokeWidth="1.5" />
      </svg>
      </div>
    );
  }

  if (shape === "grid") {
    const side = Math.sqrt(denominator);
    const size = 168;
    const cell = size / side;
    return (
      <div style={wrapperStyle}>
      <svg
        viewBox={`0 0 ${size} ${size}`}
        width="100%"
        style={{ maxWidth: 220, color: "var(--color-text)" }}
      >
        {Array.from({ length: denominator }).map((_, index) => {
          const row = Math.floor(index / side);
          const col = index % side;
          const selected = selectedSet.has(index);
          return (
            <rect
              key={index}
              x={col * cell}
              y={row * cell}
              width={cell}
              height={cell}
              fill={selected ? activeColor : inactiveColor}
              style={{ cursor: interactive ? "pointer" : "default" }}
              onClick={() => interactive && onToggleIndex && onToggleIndex(index)}
            />
          );
        })}
        {Array.from({ length: side + 1 }).map((_, index) => (
          <React.Fragment key={`grid-line-${index}`}>
            <line
              x1={index * cell}
              y1={0}
              x2={index * cell}
              y2={size}
              stroke={strokeColor}
              strokeWidth="1.5"
            />
            <line
              x1={0}
              y1={index * cell}
              x2={size}
              y2={index * cell}
              stroke={strokeColor}
              strokeWidth="1.5"
            />
          </React.Fragment>
        ))}
        <rect x={0} y={0} width={size} height={size} fill="none" stroke={strokeColor} strokeWidth="1.5" />
      </svg>
      </div>
    );
  }

  const size = 180;
  const cx = 90;
  const cy = 90;
  const radius = 78;
  const startOffset = -Math.PI / 2;
  return (
    <div style={wrapperStyle}>
    <svg
      viewBox={`0 0 ${size} ${size}`}
      width="100%"
      style={{ maxWidth: 230, color: "var(--color-text)" }}
    >
      {Array.from({ length: denominator }).map((_, index) => {
        const start = startOffset + (index * 2 * Math.PI) / denominator;
        const end = startOffset + ((index + 1) * 2 * Math.PI) / denominator;
        const selected = selectedSet.has(index);
        return (
          <path
            key={index}
            d={circleSectorPath(cx, cy, radius, start, end)}
            fill={selected ? activeColor : inactiveColor}
            stroke={strokeColor}
            strokeWidth="2"
            style={{ cursor: interactive ? "pointer" : "default" }}
            onClick={() => interactive && onToggleIndex && onToggleIndex(index)}
          />
        );
      })}
      <circle cx={cx} cy={cy} r={radius} fill="none" stroke={strokeColor} strokeWidth="2" />
    </svg>
    </div>
  );
}

function PartitionInfo({ denominator }) {
  return (
    <span>
      Rysunek jest podzielony na <InlineMath math={`${denominator}`} /> równych części.
    </span>
  );
}

function ExercisePaintShape({ task }) {
  const [selectedIndices, setSelectedIndices] = React.useState([]);

  const toggleIndex = (index) => {
    setSelectedIndices((current) =>
      current.includes(index) ? current.filter((item) => item !== index) : [...current, index].sort((a, b) => a - b)
    );
  };

  return (
    <FractionShape
      shape={task.data.shape}
      denominator={task.data.shapeDenominator || task.data.denominator}
      numerator={0}
      selectedIndices={selectedIndices}
      interactive
      onToggleIndex={toggleIndex}
    />
  );
}

function makeShapeData({ denominator, numerator }) {
  const divisor = gcd(numerator, denominator);
  return {
    shape: pickShapeForDenominator(denominator),
    numerator,
    denominator,
    simpleNumerator: numerator / divisor,
    simpleDenominator: denominator / divisor,
    divisor,
  };
}

function readFractionTask() {
  const denominator = pick([3, 4, 5, 6, 7, 8, 9, 16]);
  const possible = Array.from({ length: denominator - 1 }, (_, index) => index + 1);
  const numerator = pick(possible.filter((n) => gcd(n, denominator) === 1));
  return {
    type: "FractionReadFromShapeTask",
    data: makeShapeData({ denominator, numerator }),
    tags: ["ulamki_zwykle", "czesc_calosci"],
  };
}

function simplifyFractionTask() {
  const denominator = pick([4, 6, 8, 9, 10, 12, 15, 16]);
  const possible = Array.from({ length: denominator - 1 }, (_, index) => index + 1);
  const numerator = pick(possible.filter((n) => gcd(n, denominator) > 1));
  return {
    type: "FractionSimplifyFromShapeTask",
    data: makeShapeData({ denominator, numerator }),
    tags: ["ulamki_zwykle", "skracanie"],
  };
}

function simplifyWrittenFractionTask() {
  const candidates = [
    [2, 4],
    [3, 6],
    [4, 6],
    [5, 10],
    [5, 15],
    [6, 9],
    [6, 12],
    [8, 12],
    [8, 24],
    [10, 20],
    [12, 18],
    [12, 24],
  ];
  const [numerator, denominator] = pick(candidates);
  const divisor = gcd(numerator, denominator);
  return {
    type: "FractionSimplifyWrittenTask",
    data: {
      numerator,
      denominator,
      simpleNumerator: numerator / divisor,
      simpleDenominator: denominator / divisor,
      divisor,
    },
    tags: ["ulamki_zwykle", "skracanie", "zapis"],
  };
}

function paintFractionTask() {
  const denominator = pick([3, 4, 5, 6, 7, 8, 9, 16]);
  const possible = Array.from({ length: denominator - 1 }, (_, index) => index + 1);
  const numerator = pick(possible.filter((n) => gcd(n, denominator) === 1));
  return {
    type: "FractionPaintShapeTask",
    data: makeShapeData({ denominator, numerator }),
    tags: ["ulamki_zwykle", "zaznaczanie"],
  };
}

function paintEquivalentFractionTask() {
  const baseDenominator = pick([3, 4, 5]);
  const factor = pick([2, 3, 4].filter((item) => baseDenominator * item <= 16));
  const possible = Array.from({ length: baseDenominator - 1 }, (_, index) => index + 1);
  const baseNumerator = pick(possible.filter((n) => gcd(n, baseDenominator) === 1));
  const shapeDenominator = baseDenominator * factor;
  const targetNumerator = baseNumerator * factor;
  return {
    type: "FractionPaintEquivalentShapeTask",
    data: {
      shape: pickShapeForDenominator(shapeDenominator),
      numerator: targetNumerator,
      denominator: shapeDenominator,
      promptNumerator: baseNumerator,
      promptDenominator: baseDenominator,
      shapeDenominator,
      factor,
      simpleNumerator: baseNumerator,
      simpleDenominator: baseDenominator,
      divisor: factor,
    },
    tags: ["ulamki_zwykle", "rozszerzanie", "zaznaczanie"],
  };
}

function extendWrittenFractionTask() {
  const candidates = [
    [1, 2, 4],
    [1, 2, 6],
    [1, 2, 10],
    [2, 3, 6],
    [2, 3, 12],
    [3, 4, 8],
    [3, 4, 12],
    [2, 5, 10],
    [3, 5, 15],
    [4, 5, 20],
    [3, 7, 21],
    [5, 8, 24],
  ];
  const [numerator, denominator, targetDenominator] = pick(candidates);
  const factor = targetDenominator / denominator;
  return {
    type: "FractionExtendWrittenTask",
    data: {
      numerator,
      denominator,
      targetNumerator: numerator * factor,
      targetDenominator,
      factor,
    },
    tags: ["ulamki_zwykle", "rozszerzanie", "zapis"],
  };
}

export function generateTasks() {
  return [
    paintFractionTask(),
    readFractionTask(),
    simplifyWrittenFractionTask(),
    simplifyFractionTask(),
    paintEquivalentFractionTask(),
    extendWrittenFractionTask(),
  ];
}

export function renderTask({ type, data }) {
  if (type === "FractionPaintShapeTask") {
    return (
      <div>
        <p>
          Zaznacz na rysunku ułamek <InlineMath math={fractionLatex(data.numerator, data.denominator)} />.
          {" "}
          <PartitionInfo denominator={data.denominator} />
        </p>
      </div>
    );
  }

  if (type === "FractionPaintEquivalentShapeTask") {
    return (
      <div>
        <p>
          Zaznacz na rysunku ułamek{" "}
          <InlineMath math={fractionLatex(data.promptNumerator, data.promptDenominator)} />.
          {" "}
          <PartitionInfo denominator={data.shapeDenominator} />
        </p>
      </div>
    );
  }

  if (type === "FractionSimplifyWrittenTask") {
    return (
      <p>
        Skróć ułamek <InlineMath math={fractionLatex(data.numerator, data.denominator)} />.
        Podaj wynik w postaci nieskracalnej.
      </p>
    );
  }

  if (type === "FractionExtendWrittenTask") {
    return (
      <p>
        Rozszerz ułamek <InlineMath math={fractionLatex(data.numerator, data.denominator)} /> do mianownika{" "}
        <InlineMath math={`${data.targetDenominator}`} />.
      </p>
    );
  }

  return (
    <div>
      <p>
        {type === "FractionSimplifyFromShapeTask"
          ? "Zapisz zamalowaną część całości w postaci nieskracalnej."
          : "Zapisz, jaka część całości jest zamalowana."}
        {" "}
        <PartitionInfo denominator={data.denominator} />
      </p>
      <FractionShape shape={data.shape} denominator={data.denominator} numerator={data.numerator} />
    </div>
  );
}

export function renderExerciseTask(task) {
  if (task.type !== "FractionPaintShapeTask" && task.type !== "FractionPaintEquivalentShapeTask") {
    return renderTask(task);
  }

  return (
    <div>
      {renderTask(task)}
      <ExercisePaintShape task={task} />
    </div>
  );
}

export function renderInput(task, userAnswer, onChange, disabled) {
  if (task.type === "FractionPaintShapeTask" || task.type === "FractionPaintEquivalentShapeTask") {
    const selectedIndices = Array.isArray(userAnswer?.selectedIndices)
      ? userAnswer.selectedIndices
      : Array.from({ length: typeof userAnswer?.selectedCount === "number" ? userAnswer.selectedCount : 0 }, (_, index) => index);
    const toggleIndex = (index) => {
      const next = selectedIndices.includes(index)
        ? selectedIndices.filter((item) => item !== index)
        : [...selectedIndices, index].sort((a, b) => a - b);
      onChange({ selectedIndices: next });
    };

    return (
      <FractionShape
        shape={task.data.shape}
        denominator={task.data.shapeDenominator || task.data.denominator}
        numerator={0}
        selectedIndices={selectedIndices}
        interactive={!disabled}
        onToggleIndex={toggleIndex}
      />
    );
  }

  return (
    <FractionInput
      value={userAnswer}
      onChange={onChange}
      disabled={disabled}
      label="ułamek"
      placeholder="np. 3/8"
    />
  );
}

export function checkAnswer({ type, data }, userAnswer) {
  if (type === "FractionPaintShapeTask" || type === "FractionPaintEquivalentShapeTask") {
    const selectedIndices = Array.isArray(userAnswer?.selectedIndices)
      ? userAnswer.selectedIndices
      : Array.from({ length: typeof userAnswer?.selectedCount === "number" ? userAnswer.selectedCount : 0 }, (_, index) => index);
    return selectedIndices.length === data.numerator;
  }

  if (!userAnswer || typeof userAnswer !== "object") return false;

  if (type === "FractionExtendWrittenTask") {
    const raw = parseRawFraction(userAnswer.raw);
    if (!raw) return false;
    const exact = raw.numerator === data.targetNumerator && raw.denominator === data.targetDenominator;
    if (exact) return true;
    const expectedValue = data.numerator / data.denominator;
    const userValue = raw.numerator / raw.denominator;
    if (Math.abs(userValue - expectedValue) <= 1e-9) {
      return {
        isCorrect: false,
        score: 0,
        maxScore: 1,
        messageType: "warning",
        message: `Wartość ułamka jest poprawna, ale polecenie wymaga mianownika $${data.targetDenominator}$. Poprawna odpowiedź: $${fractionLatex(
          data.targetNumerator,
          data.targetDenominator
        )}$.`,
      };
    }
    return false;
  }

  const expectedValue = data.numerator / data.denominator;
  if (!Number.isFinite(userAnswer.parsed) || Math.abs(userAnswer.parsed - expectedValue) > 1e-9) {
    return false;
  }

  if (type !== "FractionSimplifyFromShapeTask" && type !== "FractionSimplifyWrittenTask") return true;
  const raw = parseRawFraction(userAnswer.raw);
  if (!raw) return false;
  if (gcd(raw.numerator, raw.denominator) === 1) return true;
  return {
    isCorrect: false,
    score: 0,
    maxScore: 1,
    messageType: "warning",
    message: `Wartość ułamka jest poprawna, ale w poleceniu chodzi o postać nieskracalną: $${fractionLatex(
      data.simpleNumerator,
      data.simpleDenominator
    )}$.`,
  };
}

export function renderAnswer({ type, data }) {
  if (type === "FractionPaintShapeTask" || type === "FractionPaintEquivalentShapeTask") {
    return (
      <div style={{ marginTop: 12 }}>
        <FractionShape
          shape={data.shape}
          denominator={data.shapeDenominator || data.denominator}
          numerator={data.numerator}
        />
      </div>
    );
  }

  const answer =
    type === "FractionSimplifyFromShapeTask" || type === "FractionSimplifyWrittenTask"
      ? fractionLatex(data.simpleNumerator, data.simpleDenominator)
      : type === "FractionExtendWrittenTask"
      ? fractionLatex(data.targetNumerator, data.targetDenominator)
      : fractionLatex(data.numerator, data.denominator);
  return <InlineMath math={answer} />;
}

export function renderUserAnswer(userAnswer, task) {
  if (task.type === "FractionPaintShapeTask" || task.type === "FractionPaintEquivalentShapeTask") {
    const selectedIndices = Array.isArray(userAnswer?.selectedIndices)
      ? userAnswer.selectedIndices
      : Array.from({ length: typeof userAnswer?.selectedCount === "number" ? userAnswer.selectedCount : 0 }, (_, index) => index);
    return (
      <FractionShape
        shape={task.data.shape}
        denominator={task.data.shapeDenominator || task.data.denominator}
        numerator={0}
        selectedIndices={selectedIndices}
      />
    );
  }

  if (!userAnswer || typeof userAnswer !== "object" || !userAnswer.raw) {
    return <i>(brak odpowiedzi)</i>;
  }
  return <InlineMath math={formatLatexPreview(userAnswer.raw)} />;
}

export function renderExplanation({ type, data }) {
  const raw = fractionLatex(data.numerator, data.denominator);
  const simple = fractionLatex(data.simpleNumerator, data.simpleDenominator);
  return (
    <div style={{ textAlign: "left" }}>
      {type === "FractionSimplifyWrittenTask" ? (
        <>
          <p>
            Licznik i mianownik dzielą się przez <InlineMath math={`${data.divisor}`} />.
          </p>
          <p>
            <InlineMath
              math={`${fractionLatex(data.numerator, data.denominator)}=${fractionLatex(
                `${data.numerator}:${data.divisor}`,
                `${data.denominator}:${data.divisor}`
              )}=${simple}`}
            />
          </p>
        </>
      ) : type === "FractionExtendWrittenTask" ? (
        <>
          <p>
            Aby uzyskać mianownik <InlineMath math={`${data.targetDenominator}`} />, mnożymy mianownik{" "}
            <InlineMath math={`${data.denominator}`} /> przez <InlineMath math={`${data.factor}`} />.
          </p>
          <p>
            Licznik mnożymy przez tę samą liczbę:
            {" "}
            <InlineMath
              math={`${fractionLatex(data.numerator, data.denominator)}=${fractionLatex(
                `${data.numerator}\\cdot ${data.factor}`,
                `${data.denominator}\\cdot ${data.factor}`
              )}=${fractionLatex(data.targetNumerator, data.targetDenominator)}`}
            />.
          </p>
        </>
      ) : type === "FractionPaintEquivalentShapeTask" ? (
        <>
          <p>
            Rozszerzamy ułamek do podziału widocznego na rysunku:{" "}
            <InlineMath
              math={`${fractionLatex(data.promptNumerator, data.promptDenominator)}=${fractionLatex(
                data.numerator,
                data.shapeDenominator
              )}`}
            />.
          </p>
          <p>
            Zaznaczamy <InlineMath math={`${data.numerator}`} /> z{" "}
            <InlineMath math={`${data.shapeDenominator}`} /> równych części całości.
          </p>
        </>
      ) : type === "FractionPaintShapeTask" ? (
        <p>
          Zamalowujemy <InlineMath math={`${data.numerator}`} /> z{" "}
          <InlineMath math={`${data.denominator}`} /> równych części całości.
        </p>
      ) : (
        <>
          <p>
            Zamalowano <InlineMath math={`${data.numerator}`} /> z{" "}
            <InlineMath math={`${data.denominator}`} /> równych części, czyli <InlineMath math={raw} />.
          </p>
          {type === "FractionSimplifyFromShapeTask" && (
            <p>
              Licznik i mianownik dzielimy przez <InlineMath math={`${data.divisor}`} />. Wartość ułamka się
              nie zmienia, bo skracamy licznik i mianownik przez tę samą liczbę.
            </p>
          )}
        </>
      )}
      {type === "FractionSimplifyFromShapeTask" &&
        (data.simpleNumerator !== data.numerator || data.simpleDenominator !== data.denominator ? (
          <p>
            Po skróceniu otrzymujemy <InlineMath math={simple} />.
          </p>
        ) : (
          <p>Ten ułamek jest już nieskracalny.</p>
        ))}
    </div>
  );
}

const taskAdapter = {
  renderTask,
  renderInput,
  checkAnswer,
  renderAnswer,
  renderUserAnswer,
  renderExplanation,
};

export const registry = {
  FractionReadFromShapeTask: taskAdapter,
  FractionSimplifyWrittenTask: taskAdapter,
  FractionExtendWrittenTask: taskAdapter,
  FractionSimplifyFromShapeTask: taskAdapter,
  FractionPaintShapeTask: taskAdapter,
  FractionPaintEquivalentShapeTask: taskAdapter,
};
